import { useRef, useState } from 'react'
import {
  Button,
  Card,
  Checkbox,
  Group,
  InputBase,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core'
import { IMaskInput } from 'react-imask'
import { useForm } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import { useMedplum } from '@medplum/react'
import type { Organization } from '@medplum/fhirtypes'
import { states } from '@/utils/states.ts'
import { logError } from '@/utils/errorUtils'
import { config } from '@/config'
import { RADIOLOGY_MODALITY_OPTIONS } from '@/pages/Patient/components/OrderTests/radiologyFormData'
import { FacilityLogoField } from './shared/FacilityLogoField'

const MODALITIES_EXTENSION_URL =
  'https://behuman.health/fhir/StructureDefinition/radiology-modalities'
const LOGO_EXTENSION_URL = 'https://behuman.health/fhir/StructureDefinition/organization-logo'
const GEOLOCATION_EXTENSION_URL = 'http://hl7.org/fhir/StructureDefinition/geolocation'
const WEBSITE_EXTENSION_URL = 'https://behuman.health/fhir/StructureDefinition/organization-website'

interface CreateUpdateRadiologyFacilityProps {
  organization?: Organization
  onSave?: (organization: Organization) => void
  onCancel?: () => void
}

interface RadiologyFacilityFormData {
  name: string
  phone: string
  fax: string
  website: string
  modalities: string[]
  address: {
    state: string
    zipCode: string
    city: string
    street: string
  }
}

function getExtensionValue(org: Organization | undefined, url: string): string {
  return org?.extension?.find((e) => e.url === url)?.valueString || ''
}

function getModalities(org: Organization | undefined): string[] {
  const raw = getExtensionValue(org, MODALITIES_EXTENSION_URL)
  if (!raw) return []
  try {
    return JSON.parse(raw)
  } catch {
    return []
  }
}

function getLogoReference(org: Organization | undefined): string {
  return org?.extension?.find((e) => e.url === LOGO_EXTENSION_URL)?.valueReference?.reference || ''
}

export function CreateUpdateRadiologyFacility({
  organization,
  onSave,
  onCancel,
}: CreateUpdateRadiologyFacilityProps) {
  const medplum = useMedplum()
  const [isLoading, setIsLoading] = useState(false)
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const approveOnSaveRef = useRef(false)
  const isPending = organization && organization.active === false

  const form = useForm<RadiologyFacilityFormData>({
    initialValues: {
      name: organization?.name || '',
      phone: organization?.telecom?.find((t) => t.system === 'phone')?.value || '',
      fax: organization?.telecom?.find((t) => t.system === 'fax')?.value || '',
      website: getExtensionValue(organization, WEBSITE_EXTENSION_URL),
      modalities: getModalities(organization),
      address: {
        state: organization?.address?.[0]?.state || '',
        zipCode: organization?.address?.[0]?.postalCode || '',
        city: organization?.address?.[0]?.city || '',
        street: organization?.address?.[0]?.line?.[0] || '',
      },
    },
    validate: {
      name: (value) => (!value?.trim() ? 'Facility name is required' : null),
      fax: (value) => {
        if (!value) return null
        const digits = value.replace(/\D/g, '')
        return digits.length === 10 ? null : 'Invalid fax number'
      },
      address: {
        state: (value) => (!value?.trim() ? 'State is required' : null),
        zipCode: (value) => {
          if (!value?.trim()) return 'ZIP code is required'
          const digits = value.replace(/\D/g, '')
          if (!(digits.length === 5 || digits.length === 9)) {
            return 'ZIP code must be 5 or 9 digits'
          }
          return null
        },
        city: (value) => (!value?.trim() ? 'City is required' : null),
        street: (value) => (!value?.trim() ? 'Street address is required' : null),
      },
    },
  })

  const handleSubmit = async (values: RadiologyFacilityFormData) => {
    let zip = values.address.zipCode || ''
    const digits = zip.replace(/\D/g, '')
    if (digits.length === 9) {
      zip = `${digits.slice(0, 5)}-${digits.slice(5)}`
    } else if (digits.length === 5) {
      zip = digits
    }

    setIsLoading(true)
    try {
      // Geocode address
      let geoExtension: Organization['address'] extends (infer U)[]
        ? NonNullable<U>['extension']
        : never = undefined
      const fullAddress = [values.address.street, values.address.city, values.address.state, zip]
        .filter(Boolean)
        .join(', ')
      const token = medplum.getAccessToken()
      const baseUrl = config.middlewareUrl || ''

      if (baseUrl) {
        try {
          const resp = await fetch(
            `${baseUrl}/order/geocode?q=${encodeURIComponent(fullAddress)}`,
            { headers: token ? { Authorization: `Bearer ${token}` } : {} },
          )
          if (resp.ok) {
            const coords = (await resp.json()) as { lat: number; lon: number } | null
            if (coords?.lat != null && coords?.lon != null) {
              geoExtension = [
                {
                  url: GEOLOCATION_EXTENSION_URL,
                  extension: [
                    { url: 'latitude', valueDecimal: coords.lat },
                    { url: 'longitude', valueDecimal: coords.lon },
                  ],
                },
              ]
            }
          }
        } catch (err) {
          console.warn('Geocoding failed:', err)
        }
      }

      // Upload logo if provided
      let logoReference = getLogoReference(organization)
      if (logoFile) {
        const arrayBuffer = await logoFile.arrayBuffer()
        const base64 = btoa(
          new Uint8Array(arrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), ''),
        )
        const binary = await medplum.createResource({
          resourceType: 'Binary',
          contentType: logoFile.type,
          data: base64,
        })
        if (binary.id) {
          logoReference = `Binary/${binary.id}`
        }
      }

      const faxDigits = values.fax.replace(/\D/g, '')

      // Preserve existing geolocation if geocoding didn't run
      if (!geoExtension && organization?.address?.[0]?.extension) {
        geoExtension = organization.address[0].extension
      }

      const extensions: Organization['extension'] = [
        {
          url: MODALITIES_EXTENSION_URL,
          valueString: JSON.stringify(values.modalities),
        },
      ]

      if (values.website.trim()) {
        extensions.push({
          url: WEBSITE_EXTENSION_URL,
          valueString: values.website.trim(),
        })
      }

      if (logoReference) {
        extensions.push({
          url: LOGO_EXTENSION_URL,
          valueReference: { reference: logoReference },
        })
      }

      // Preserve active status; set true if approving
      const activeStatus = approveOnSaveRef.current ? true : (organization?.active ?? true)

      const organizationData: Organization = {
        resourceType: 'Organization',
        active: activeStatus,
        name: values.name,
        type: [
          {
            coding: [
              {
                system: 'http://terminology.hl7.org/CodeSystem/organization-type',
                code: 'prov',
                display: 'Healthcare Provider',
              },
            ],
          },
          {
            coding: [
              {
                system: 'https://behuman.health/organization-type',
                code: 'radiology-facility',
                display: 'Radiology Facility',
              },
            ],
          },
        ],
        telecom: [
          ...(faxDigits ? [{ system: 'fax' as const, value: values.fax }] : []),
          ...(values.phone.trim() ? [{ system: 'phone' as const, value: values.phone }] : []),
        ],
        address: [
          {
            line: [values.address.street],
            city: values.address.city,
            state: values.address.state,
            postalCode: zip,
            country: 'US',
            extension: geoExtension,
          },
        ],
        extension: extensions,
      }

      if (!organization) {
        const behumanProject = await medplum.searchOne('Project', 'name:contains=behuman')
        if (!behumanProject) {
          throw new Error('Failed to get behuman project')
        }
        organizationData.meta = { project: behumanProject.id }
      } else if (organization.id) {
        organizationData.id = organization.id
        if (organization.meta?.project) {
          organizationData.meta = {
            ...organization.meta,
            project: organization.meta.project,
          }
        }
      }

      let result: Organization
      if (!organization) {
        result = await medplum.createResource(organizationData)
      } else {
        result = await medplum.updateResource(organizationData)
      }

      notifications.show({
        title: 'Success',
        message: `Radiology facility ${!organization ? 'created' : 'updated'} successfully`,
        color: 'green',
      })

      onSave?.(result)
    } catch (error) {
      logError(
        'super',
        `Error ${!organization ? 'creating' : 'updating'} radiology facility`,
        error,
      )
      notifications.show({
        title: 'Error',
        message: `Failed to ${!organization ? 'create' : 'update'} radiology facility. Please try again.`,
        color: 'red',
      })
    } finally {
      setIsLoading(false)
      approveOnSaveRef.current = false
    }
  }

  return (
    <Card withBorder className="mx-auto max-w-2xl">
      <Title order={2} mb="md">
        {!organization ? 'Add Radiology Facility' : 'Update Radiology Facility'}
      </Title>

      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <TextInput
            label="Facility Name"
            placeholder="e.g. AdventHealth Daytona Beach"
            {...form.getInputProps('name')}
            required
          />

          <Group grow>
            <TextInput
              label="Street Address"
              placeholder="Street address"
              {...form.getInputProps('address.street')}
              required
            />
            <TextInput
              label="City"
              placeholder="City"
              {...form.getInputProps('address.city')}
              required
            />
          </Group>
          <Group grow>
            <Select
              label="State"
              placeholder="Select state"
              data={states}
              {...form.getInputProps('address.state')}
              required
              searchable
            />
            <InputBase
              label="ZIP Code"
              placeholder="00000 or 12345-1234"
              component={IMaskInput}
              mask={[{ mask: '00000' }, { mask: '00000-0000' }]}
              {...form.getInputProps('address.zipCode')}
              required
            />
          </Group>

          <Group grow>
            <InputBase
              label="Phone"
              placeholder="(000) 000-0000"
              component={IMaskInput}
              mask="(000) 000-0000"
              value={form.values.phone}
              onAccept={(value) => form.setFieldValue('phone', value)}
              error={form.errors.phone}
            />
            <InputBase
              label="Fax"
              placeholder="(000) 000-0000"
              component={IMaskInput}
              mask="(000) 000-0000"
              value={form.values.fax}
              onAccept={(value) => form.setFieldValue('fax', value)}
              error={form.errors.fax}
            />
          </Group>

          <TextInput
            label="Website"
            placeholder="https://example.com"
            {...form.getInputProps('website')}
          />

          <FacilityLogoField
            value={logoFile}
            onChange={setLogoFile}
            existingLogoRef={getLogoReference(organization)}
          />

          <div>
            <Text size="sm" fw={500} mb="xs">
              Available Modalities
            </Text>
            <Checkbox.Group
              value={form.values.modalities}
              onChange={(value) => form.setFieldValue('modalities', value)}
            >
              <SimpleGrid cols={3} spacing="xs">
                {RADIOLOGY_MODALITY_OPTIONS.map((m) => (
                  <Checkbox key={m.key} value={m.key} label={m.label} />
                ))}
              </SimpleGrid>
            </Checkbox.Group>
          </div>

          <Group justify="flex-end" mt="xl">
            {onCancel && (
              <Button variant="subtle" onClick={onCancel}>
                Cancel
              </Button>
            )}
            <Button type="submit" loading={isLoading}>
              {!organization ? 'Add Facility' : 'Update Facility'}
            </Button>
            {isPending && (
              <Button
                color="green"
                loading={isLoading}
                onClick={() => {
                  approveOnSaveRef.current = true
                  form.onSubmit(handleSubmit)()
                }}
              >
                Approve & Save
              </Button>
            )}
          </Group>
        </Stack>
      </form>
    </Card>
  )
}
